import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { LiveTvChannel, LiveTvProgram } from '../../api/types';
import { programmeTimes } from './programmeTimes';

/// One programme, and the way into it: the Live TV guide and the search open the same one.
export default function ProgrammeDialog({
  opened,
  onClose,
  onWatch,
}: {
  opened: { program: LiveTvProgram; channel: LiveTvChannel } | undefined;
  onClose: () => void;
  onWatch: (channel: LiveTvChannel) => void;
}) {
  const { t, i18n } = useTranslation();

  return (
    <Dialog open={Boolean(opened)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent>
        {opened && (
          <Stack spacing={2}>
            <Typography variant="h3">{opened.program.title}</Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
              <Chip size="small" label={opened.channel.name} />
              <Chip size="small" variant="outlined" label={programmeTimes(opened.program, i18n.language)} />
              {opened.program.genres.map((genre) => (
                <Chip key={genre} size="small" label={genre} />
              ))}
            </Stack>

            {opened.program.episodeTitle && (
              <Typography color="text.secondary">{opened.program.episodeTitle}</Typography>
            )}

            {opened.program.description && <Typography>{opened.program.description}</Typography>}

            <Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={() => onWatch(opened.channel)}
              >
                {t('livetv.watch')}
              </Button>
            </Box>

            {/* Said plainly: a guide is a schedule, not a recorder. */}
            <Typography variant="caption" color="text.secondary">
              {t('livetv.watchHint')}
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
